import { FEATURE_CARD_OTHER_COLOR } from "../constants";

import { i18n } from "../../../i18n";

import { formatAttributeValue } from "../../../utils/formatAttributeValue";
import { getLayerDefinition } from "../../../evergis/core/layer/getLayerDefinition";
import { getColorGradient } from "./getColorGradient";

import { ConfigContainerChild, ConfigFilter, ConfigRelatedDataSource, FilterItem } from "../types";
import { FeatureDataSource } from "../../../ducks/showFeature/types";
import { LayerInfo } from "../../../interfaces/resourceInfo";

export const getDataFromRelatedFeatures = ({
  config,
  filters,
  relatedConfig,
  dataSource,
  layerInfo,
}: {
  config: ConfigContainerChild;
  filters: ConfigFilter[];
  relatedConfig: ConfigRelatedDataSource;
  dataSource: FeatureDataSource;
  layerInfo: LayerInfo;
}): FilterItem[] | null => {
  if (!dataSource || !dataSource?.features?.length) {
    return [];
  }

  const { options } = config || {};
  const defaultColor = options?.defaultColor || FEATURE_CARD_OTHER_COLOR;
  const layerDefinition = getLayerDefinition(layerInfo);
  let data = JSON.parse(JSON.stringify(dataSource?.features));
  const sortAttribute = options?.orderByValue
    ? relatedConfig.attributeName
    : options?.orderByTitle
    ? relatedConfig.attributeTitle
    : null;

  if (sortAttribute) {
    data.sort((a, b) => b.attributes[sortAttribute] - a.attributes[sortAttribute]);
  }

  const isOtherSliced = options?.otherItems && options.otherItems < data.length;
  const otherValue = isOtherSliced
    ? data.slice(options.otherItems).reduce((prev, { attributes }) => prev + attributes[relatedConfig.attributeName], 0)
    : null;

  if (isOtherSliced) {
    data = data.slice(0, options?.otherItems);
  }

  const gradientArray = getColorGradient(options?.colors, data.length);

  const result = data.reduce((acc, feature, index) => {
    if (!relatedConfig) return acc;

    const filter = relatedConfig?.filterName && filters?.find(({ name }) => name === relatedConfig.filterName);

    if (relatedConfig?.filterName && !filter) {
      return acc;
    }

    const attributeName = feature.attributes[relatedConfig.attributeName];
    const attributeTitle = feature.attributes[relatedConfig.attributeTitle];
    const attributeColor = feature.attributes[relatedConfig.attributeColor];

    return [
      ...acc,
      {
        name: formatAttributeValue(
          layerDefinition.attributes[relatedConfig.attributeTitle]?.type,
          attributeTitle,
          layerDefinition.attributes[relatedConfig.attributeTitle]?.stringFormat,
        ),
        value: attributeName === null ? null : Number(attributeName),
        color: attributeColor || gradientArray?.[index] || defaultColor,
        min: filter?.attributeMin ? feature.attributes[filter.attributeMin] : null,
        max: filter?.attributeMin ? feature.attributes[filter.attributeMax] : null,
      },
    ];
  }, []);

  if (isOtherSliced) {
    result.push({
      name: i18n.t("classification.other", { ns: "common" }),
      color: defaultColor,
      value: Number(otherValue),
    });
  }

  return result;
};
